import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { InputLabel, FormControl, Select, MenuItem, Box } from "@mui/material";
import {
  fetchHarvestDetailDropdown,
  fetchFilteredHarvestDetails,
  fetchHarvestFieldsDropdown,
} from "../../slices/harvestdetail.slice";
import { RootState } from "../../store";
import { HarvestDetailSpreadsheetComponent } from "./HarvestDetailSpreadsheet";
import { MultiSelectDropdown } from "../../components/MultiSelect/MultiSelectDropdownComponent";
import CustomButton from "../../components/Button";

const HarvestDetailManagement: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [selectedLandlords, setSelectedLandlords] = useState<any[]>([]);
  const [selectedCrops, setSelectedCrops] = useState<any[]>([]);
  const [selectedFields, setSelectedFields] = useState<any[]>([]);

  const dispatch = useAppDispatch();

  const {
    harvestSpreadsheet,
    availableYears,
    availableLandlords,
    availableCrops,
    availableFields,
    loading,
    loadingDropdowns,
  } = useAppSelector((state: RootState) => state.harvestdetail);

  // Load dropdown data once on mount
  useEffect(() => {
    dispatch(fetchHarvestDetailDropdown("availableYears"));
    dispatch(fetchHarvestDetailDropdown("availableLandlords"));
    dispatch(fetchHarvestDetailDropdown("availableCrops"));
  }, [dispatch]);

  // Initialize selections when data is loaded
  useEffect(() => {
    if (
      availableLandlords.length > 0 &&
      availableCrops.length > 0 &&
      availableYears.length > 0
    ) {
      setSelectedLandlords(availableLandlords);
      setSelectedCrops(availableCrops);

      const landlordIdsArray = availableLandlords.map((value) => value.id);
      dispatch(fetchHarvestFieldsDropdown(landlordIdsArray));

      // Set year if current selection is not available
      if (!availableYears.find((year) => year.id === selectedYear)) {
        const firstYear = availableYears[0]?.id || 2025;
        setSelectedYear(firstYear);
      }

      // Load initial data
      dispatch(
        fetchFilteredHarvestDetails({
          year: selectedYear,
          landlordIds: availableLandlords.map((x) => x.id),
          cropIds: availableCrops.map((x) => x.id),
          fieldIds: availableFields.map((x) => x.id),
        })
      );
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    availableLandlords,
    availableCrops,
    availableYears,
    selectedYear,
  ]);

  useEffect(() => {
    setSelectedFields(availableFields);
  }, [availableFields]);

  const handleSearch = () => {
    const landlordIdsArray = selectedLandlords.map((value) => value.id);
    const cropIdsArray =
      selectedCrops.length > 0
        ? selectedCrops.map((value) => value.id)
        : undefined;
    const fieldIdsArray =
      selectedFields.length > 0
        ? selectedFields.map((value) => value.id)
        : undefined;

    const requestData = {
      year: selectedYear,
      landlordIds: landlordIdsArray,
      ...(cropIdsArray && { cropIds: cropIdsArray }),
      ...(fieldIdsArray && { fieldIds: fieldIdsArray }),
    };

    dispatch(fetchFilteredHarvestDetails(requestData));
  };

  const handleYearChange = (event: any) => {
    const newYear = parseInt(event.target.value);
    setSelectedYear(newYear);
  };

  const onLandlordsChange = (newSelection: any[]) => {
    setSelectedLandlords(newSelection);
    const landlordIdsArray = newSelection.map((value) => value.id);
    dispatch(fetchHarvestFieldsDropdown(landlordIdsArray));
  };

  const handleSaveSuccess = () => {
    const requestData = {
      year: selectedYear,
      landlordIds: selectedLandlords.map((value) => value.id),
      ...(selectedCrops.length > 0 && {
        cropIds: selectedCrops.map((value) => value.id),
      }),
      ...(selectedFields.length > 0 && {
        fieldIds: selectedFields.map((value) => value.id),
      }),
    };

    dispatch(fetchFilteredHarvestDetails(requestData));
  };

  // Check if search should be disabled
  const isSearchDisabled =
    !selectedLandlords.length ||
    !selectedCrops.length ||
    !selectedFields.length ||
    !selectedYear ||
    loadingDropdowns;

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2} alignItems={"center"} flexWrap={"wrap"}>
        <FormControl>
          <InputLabel>Year</InputLabel>
          <Select
            labelId=""
            value={selectedYear}
            label="Year"
            onChange={handleYearChange}
            size="small"
          >
            {availableYears.map((year) => (
              <MenuItem key={year.id} value={year.id}>
                {year.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <MultiSelectDropdown
          options={availableCrops}
          label="Crop"
          selected={selectedCrops}
          setSelected={setSelectedCrops}
        />

        <MultiSelectDropdown
          options={availableLandlords}
          label="Landlord"
          selected={selectedLandlords}
          setSelected={onLandlordsChange}
        />

        <MultiSelectDropdown
          options={availableFields}
          label="Field"
          selected={selectedFields}
          setSelected={setSelectedFields}
        />

        <CustomButton
          onClick={handleSearch}
          sx={{ width: "110px" }}
          disabled={isSearchDisabled}
        >
          Search
        </CustomButton>
      </Box>

      <HarvestDetailSpreadsheetComponent
        data={harvestSpreadsheet}
        selectedYear={selectedYear}
        selectedLandlords={selectedLandlords}
        selectedCrops={selectedCrops}
        selectedFields={selectedFields}
        loading={loading}
        onSaveSuccess={handleSaveSuccess}
      />
    </Box>
  );
};

export default HarvestDetailManagement;